import { fg } from '@confluence/feature-gating';

export type ExtensionDimensionSource = 'confluence:pageBanner' | 'atl.general';
type Dimensions = { height: number; width: number };
type Registration = {
	source: ExtensionDimensionSource;
	extensionId: string;
	dimensions?: Dimensions;
};

const registrations = new Map<Element, Registration>();
let observer: ResizeObserver | undefined;

// Bound the addition to the existing UFO payload, even on tenants with many apps.
const MAX_REPORTED_EXTENSIONS = 50;

/** Observe the existing host wrapper, without querying inside a vendor iframe. */
export const observeExtensionDimensions = (
	element: Element,
	source: ExtensionDimensionSource,
	extensionId: string,
): (() => void) | undefined => {
	if (!extensionId || !window.ResizeObserver || !fg('confluence_ufo_banner_extension_dimensions')) {
		return;
	}

	if (!observer) {
		observer = new ResizeObserver((entries) => {
			if (!fg('confluence_ufo_banner_extension_dimensions')) {
				return;
			}
			for (const entry of entries) {
				const registration = registrations.get(entry.target);
				if (!registration) {
					continue;
				}
				// These wrappers have no padding/border. contentRect supplies physical CSS
				// pixels after layout, without synchronous layout reads or React updates.
				// This measures allocated host space, including any active preset height.
				const { height, width } = entry.contentRect;
				if (Number.isFinite(height) && Number.isFinite(width) && height >= 0 && width >= 0) {
					registration.dimensions = { height: Math.round(height), width: Math.round(width) };
				}
			}
		});
	}

	const registration: Registration = { source, extensionId };
	registrations.set(element, registration);
	observer.observe(element);

	return () => {
		if (registrations.get(element) !== registration) {
			return;
		}
		observer?.unobserve(element);
		registrations.delete(element);
		if (registrations.size === 0) {
			observer?.disconnect();
			observer = undefined;
		}
	};
};

/** Latest observations at payload construction; later resizes cannot mutate the snapshot. */
export const getExtensionDimensions = (): {
	extensionDimensions: Partial<Record<ExtensionDimensionSource, Record<string, Dimensions>>>;
	extensionDimensionsOmittedCount?: number;
} => {
	const groups = new Map<ExtensionDimensionSource, Map<string, Dimensions>>();
	for (const [element, { source, extensionId, dimensions }] of registrations) {
		if (!element.isConnected || !dimensions) {
			continue;
		}
		const group = groups.get(source) ?? new Map<string, Dimensions>();
		group.set(extensionId, { ...dimensions });
		groups.set(source, group);
	}
	let reported = 0;
	let omitted = 0;
	const extensionDimensions = Object.fromEntries(
		Array.from(groups, ([source, dimensions]) => [
			source,
			Object.fromEntries(
				Array.from(dimensions).filter(() => {
					if (reported < MAX_REPORTED_EXTENSIONS) {
						reported++;
						return true;
					}
					omitted++;
					return false;
				}),
			),
		]),
	);
	return {
		extensionDimensions,
		...(omitted ? { extensionDimensionsOmittedCount: omitted } : {}),
	};
};
